<template>
  <el-popover
    ref="popover"
    v-model="visible"
    class="linkTypePicker__root"
    trigger="click"
    placement="bottom-start"
    :visible-arrow="false"
    popper-class="linkTypePickerDropdown"
    @show="onShow"
    @hide="onHide"
  >
    <div class="linkTypePicker">
      <ul class="linkTypePicker__menu">
        <li
          v-for="node in tree"
          :key="node.value"
          class="linkTypePicker__node"
          :class="{
            'is-expanded': expanded === node.value,
            'is-active': isTypeNodeActive(draft, node.value),
          }"
          @click="onNodeClick(node)"
        >
          <span class="linkTypePicker__label">{{ node.label }}</span>
          <i v-if="node.children" class="el-icon-arrow-right linkTypePicker__arrow"></i>
        </li>
        <li v-if="!tree.length" class="linkTypePicker__empty">无数据</li>
      </ul>
      <div v-if="hasGroups" class="linkTypePicker__menu linkTypePicker__menu--children">
        <template v-if="expandedNode">
          <div class="linkTypePicker__head">
            <span class="linkTypePicker__headTitle">{{ expandedNode.label }}</span>
            <span class="linkTypePicker__mode">
              多选
              <el-switch v-model="multiple" />
            </span>
          </div>
          <ul>
            <li
              v-for="child in expandedChildren"
              :key="child.value"
              class="linkTypePicker__node"
              :class="{ 'is-active': isTypeChildSelected(draft, expandedNode.value, child.value) }"
              @click="onChildClick(child.value)"
            >
              <el-checkbox
                v-if="multiple"
                class="linkTypePicker__check"
                :value="isTypeChildSelected(draft, expandedNode.value, child.value)"
                @click.native.prevent
              />
              <span class="linkTypePicker__label">{{ child.label }}</span>
            </li>
          </ul>
        </template>
      </div>
    </div>
    <el-input
      slot="reference"
      ref="input"
      class="linkTypePicker__input"
      :class="{ 'is-open': visible }"
      :value="displayText"
      :placeholder="placeholder"
      :size="size"
      readonly
      @mouseenter.native="hovering = true"
      @mouseleave.native="hovering = false"
    >
      <i slot="suffix" class="el-input__icon" :class="suffixIcon" @click="onSuffixClick"></i>
    </el-input>
  </el-popover>
</template>

<script>
import {
  TYPE_ALL,
  buildTypeTree,
  isTypeChildSelected,
  isTypeNodeActive,
  parseTypeSelection,
  selectTypeChild,
  typeSelectionLabel,
} from "./resourceTypeFilter";

/**
 * 资源类型两栏筛选：左栏单击，有二级的在右栏展开，没有二级的直接选中且右栏变空；悬停不展开。
 * 只要有分组就固定两栏，面板大小不跳；再次打开时右栏对应当前选中值，没有选中或选的是没有二级的项时右栏空白。
 * 右栏 All 与其它项互斥，右上角切换多选。单选点一项即收起；多选收起面板时才回传一次，避免每勾一下就查询。
 * v-model 值的格式见 resourceTypeFilter.js。
 */
export default {
  name: "ResourceTypePicker",
  model: { prop: "value", event: "change" },
  props: {
    value: { type: String, default: "" },
    /** 接口返回的类型名列表，分组与 stickers 子类型由 buildTypeTree 组装。 */
    types: { type: Array, default: () => [] },
    /** 单选类型按字母排；传 false 保留 types 的顺序（如壁纸分类的写死列表）。 */
    sortTypes: { type: Boolean, default: true },
    placeholder: { type: String, default: "请选择资源类型" },
    size: { type: String, default: "small" },
  },
  data() {
    return {
      visible: false,
      hovering: false,
      /** 面板打开期间的选中值；收起时与 value 不同才回传。 */
      draft: this.value,
      /** 左栏最近点的项；是没有二级的类型时右栏空白。 */
      expanded: "",
      /** 一个开关管所有一级，组件在页面上就一直记住；默认单选。 */
      multiple: false,
    };
  },
  computed: {
    tree() {
      return buildTypeTree(this.types, { sortTypes: this.sortTypes });
    },
    /** 壁纸分类等全是单层类型时不留空的右栏。 */
    hasGroups() {
      return this.tree.some((node) => node.children);
    },
    expandedNode() {
      return this.tree.find((node) => node.value === this.expanded && node.children) || null;
    },
    expandedChildren() {
      if (!this.expandedNode) return [];
      return [{ value: TYPE_ALL, label: "All" }, ...this.expandedNode.children];
    },
    displayText() {
      return typeSelectionLabel(this.visible ? this.draft : this.value);
    },
    suffixIcon() {
      if (this.hovering && this.value) return "el-icon-circle-close linkTypePicker__clear";
      return ["el-icon-arrow-down", "linkTypePicker__caret", { "is-reverse": this.visible }];
    },
  },
  watch: {
    value(next) {
      if (!this.visible) this.draft = next;
    },
  },
  methods: {
    isTypeNodeActive,
    isTypeChildSelected,
    onShow() {
      this.draft = this.value;
      const current = parseTypeSelection(this.value);
      this.expanded = current ? current.parent || current.leaf : "";
      const popper = this.$refs.popover.$refs.popper;
      if (popper) popper.style.minWidth = `${this.$refs.input.$el.offsetWidth}px`;
    },
    onHide() {
      if (this.draft !== this.value) this.$emit("change", this.draft);
    },
    onNodeClick(node) {
      this.expanded = node.value;
      if (node.children) return;
      this.draft = node.value;
      this.visible = false;
    },
    onChildClick(child) {
      this.draft = selectTypeChild(this.draft, this.expandedNode.value, child, this.multiple);
      if (!this.multiple) this.visible = false;
    },
    onSuffixClick(event) {
      if (!(this.hovering && this.value)) return;
      // 清空不打开面板：拦住冒泡，popover 的 reference 点击才不会切换显示
      event.stopPropagation();
      this.draft = "";
      this.$emit("change", "");
    },
  },
};
</script>

<style scoped>
/* el-popover 外层是行内 span，宽度（filterControl 的 100%、外部传入的 width）要它是 inline-block 才生效 */
.linkTypePicker__root {
  display: inline-block;
  vertical-align: middle;
}
.linkTypePicker__input {
  cursor: pointer;
}
.linkTypePicker__input >>> .el-input__inner {
  cursor: pointer;
}
.linkTypePicker__input.is-open >>> .el-input__inner {
  border-color: var(--link-accent, #2563eb);
}
.linkTypePicker__caret {
  transition: transform 0.2s;
}
.linkTypePicker__caret.is-reverse {
  transform: rotate(180deg);
}
.linkTypePicker__clear {
  cursor: pointer;
}
</style>
